import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';

export interface TemplateListItem {
  id: string;
  label: string;
  body: string;
  source?: 'wexler' | 'personal';
  matterId?: string;
  icon?: string;
  createdDate?: Date;
}

interface TemplateListProps {
  templates: TemplateListItem[];
  emptyMessage: string;
  onSelect: (template: TemplateListItem) => void;
  onEdit?: (template: TemplateListItem) => void;
  onDelete?: (template: TemplateListItem) => void;
}

interface SearchableTemplateListProps extends TemplateListProps {
  searchPlaceholder?: string;
}

export function SearchableTemplateList({
  templates,
  emptyMessage,
  onSelect,
  onEdit,
  onDelete,
  searchPlaceholder = 'Search templates',
}: SearchableTemplateListProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return templates;
    return templates.filter((t) => t.label.toLowerCase().includes(trimmed) || t.body.toLowerCase().includes(trimmed));
  }, [templates, query]);

  if (templates.length === 0) {
    return <TemplateList templates={templates} emptyMessage={emptyMessage} onSelect={onSelect} />;
  }

  return (
    <div className="space-y-2">
      <SearchInput
        size="sm"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="m-2 mb-0"
      />
      <TemplateList
        templates={filtered}
        emptyMessage={query.trim() ? 'No matching templates' : emptyMessage}
        onSelect={onSelect}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

function renderTemplateBody(body: string) {
  const parts = body.split(/(%\[[^\]]*\])/g);
  return parts.map((part, index) => {
    const match = part.match(/^%\[([^\]]*)\]$/);
    if (match) {
      return (
        <span key={index} className="text-green-500">
          [{match[1]}]
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

export function TemplateList({ templates, emptyMessage, onSelect, onEdit, onDelete }: TemplateListProps) {
  const { i18n } = useTranslation();

  if (templates.length === 0) {
    return <p className="px-4 py-3 text-[13px] text-gray-400">{emptyMessage}</p>;
  }

  const hasActions = !!(onEdit || onDelete);

  return (
    <div className="max-h-56 space-y-1 overflow-y-auto p-2">
      {templates.map((template) => (
        <div
          key={template.id}
          className="group flex items-start rounded-sm text-[13px] text-gray-700 duration-150 hover:bg-gray-50"
        >
          <button onClick={() => onSelect(template)} className="flex-1 items-center space-y-1 px-2 py-1 text-left">
            <div className="flex items-center gap-1.5 font-medium">
              {template.icon && <Icon className={cn(template.icon, 'mb-px text-gray-600')} />}{' '}
              <p className="line-clamp-1">{template.label}</p>
            </div>

            <div className="flex gap-1">
              {template.createdDate && (
                <p className="text-xs whitespace-nowrap text-gray-400">
                  {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(
                    new Date(template.createdDate),
                  )}{' '}
                  ·
                </p>
              )}
              <p className="line-clamp-1 text-xs text-gray-500">{renderTemplateBody(template.body)}</p>
            </div>
          </button>

          {hasActions && (
            <DropdownMenu>
              <DropdownMenuTrigger onClick={(e) => e.stopPropagation()} className="mt-1.75 mr-1.5 ml-1">
                <Icon className="lucide--more-horizontal" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={20} side="right" className="-mt-1 min-w-24">
                {onEdit && (
                  <DropdownMenuItem
                    className="text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(template);
                    }}
                  >
                    <Icon className="lucide--pencil text-[11px]" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    className="text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(template);
                    }}
                  >
                    <Icon className="lucide--trash text-[11px]" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
    </div>
  );
}
